import {useEffect, useState} from 'react';
import {fetchUploadConfig} from './sourcesApi';

const DEFAULT_CONFIG = {urlImportEnabled: false};

// Module-level cache so the config is fetched once per session and shared across all
// Sources panel instances. The in-flight promise is cached to dedupe concurrent mounts.
let cachedConfig = null;
let inFlightPromise = null;

function loadUploadConfig() {
    if (cachedConfig) {
        return Promise.resolve(cachedConfig);
    }
    if (!inFlightPromise) {
        inFlightPromise = fetchUploadConfig()
            .then((config) => {
                cachedConfig = config;
                return config;
            })
            .catch((error) => {
                // Fail closed: hide web import if the config can't be loaded.
                cachedConfig = DEFAULT_CONFIG;
                return DEFAULT_CONFIG;
            })
            .finally(() => {
                inFlightPromise = null;
            });
    }
    return inFlightPromise;
}

/**
 * Returns the effective Sources-panel upload config `{ urlImportEnabled, loading }`.
 * Fetched once and cached for the session.
 */
export function useUploadConfig() {
    const [config, setConfig] = useState(cachedConfig || DEFAULT_CONFIG);
    const [loading, setLoading] = useState(!cachedConfig);

    useEffect(() => {
        if (cachedConfig) {
            setConfig(cachedConfig);
            setLoading(false);
            return undefined;
        }
        let active = true;
        loadUploadConfig().then((loaded) => {
            if (active) {
                setConfig(loaded);
                setLoading(false);
            }
        });
        return () => {
            active = false;
        };
    }, []);

    return {...config, loading};
}

export default useUploadConfig;
